import executeTicketCommand from '@chama/utils/functions/execute-ticket-command';
import Raven from '@app/sentry';
import config from '@app/config';
import AppInsights from '@app/appInsights';
import values from 'lodash.values';
import { fetchDrivers } from './api-drivers';
import track from '../util/analytics-handler';

export const ADD_OR_UPDATE_TICKETS = 'ADD_OR_UPDATE_TICKETS';
export const SET_CURRENT_ORDER_ID = 'SET_CURRENT_ORDER_ID';
export const ORDER_ASSIGNMENT_LOADING = 'ORDER_ASSIGNMENT_LOADING';
export const ORDER_ASSIGNMENT_DONE = 'ORDER_ASSIGNMENT_DONE';
export const START_PROCESSING_ORDER = 'START_PROCESSING_ORDER';
export const PROCESSING_ORDER_ERROR = 'PROCESSING_ORDER_ERROR';
export const UPDATE_ORDER_QUEUE = 'UPDATE_ORDER_QUEUE';

export const switchOrderQueueView = queue => dispatch => {
  clearTimeout(ticketsTimeout);
  dispatch(updateOrderQueue(queue));
  dispatch(fetchTicketsByQueue());
};

const updateOrderQueue = queue => ({
  type: UPDATE_ORDER_QUEUE,
  payload: { queue },
});

let ticketsTimeout = null;
export const fetchTicketsByQueue = () => (dispatch, getState, { api }) => {
  const {
    api: {
      dt: { fetchTicketsDelay },
    },
  } = config;

  const queue = getState().orders.selectedOrderQueue;

  return api.fetchTicketsByQueue(queue).then(tickets => {
    dispatch(addOrUpdateTickets(tickets));
    ticketsTimeout = setTimeout(() => dispatch(fetchTicketsByQueue()), fetchTicketsDelay);
  });
};

export const fetchOrderDetails = (dealerId, orderId) => async (dispatch, getState, { api }) => {
  const details = await api.fetchOrderDetails(dealerId, orderId);
  return details;
};

const addOrUpdateTickets = tickets => ({
  type: ADD_OR_UPDATE_TICKETS,
  tickets,
});

const startProcessingOrder = orderId => {
  return {
    type: START_PROCESSING_ORDER,
    orderId,
  };
};

const processingOrderError = orderId => {
  return {
    type: PROCESSING_ORDER_ERROR,
    orderId,
  };
};

const startExecutingAssignment = () => {
  return {
    type: ORDER_ASSIGNMENT_LOADING,
  };
};

const doneExecutingAssignment = () => {
  return {
    type: ORDER_ASSIGNMENT_DONE,
  };
};

const compareTimeNewestFirst = (a, b) => (a > b ? -1 : 1);

export const fetchMoreFinalizedTickets = () => (dispatch, getState, { api }) => {
  const ticketCreationTimes = values(getState().orders.dict)
    .map(t => t.creationTime)
    .sort(compareTimeNewestFirst);
  const oldestCreationTime = ticketCreationTimes[ticketCreationTimes.length - 1];

  return api.fetchTicketsBefore(oldestCreationTime).then(tickets => {
    dispatch(addOrUpdateTickets(tickets));
  });
};

const fetchTickets = since => (dispatch, getState, { api }) => {
  const {
    api: {
      dt: { fetchTicketsDelay },
    },
  } = config;

  return api.fetchTicketsSince(since).then(tickets => {
    let nextSince = since;

    try {
      dispatch(addOrUpdateTickets(tickets));

      if (tickets.length) {
        const {
          user: {
            loggedUser: { uid: userId },
          },
        } = getState();

        tickets.forEach(ticket => {
          const {
            data: {
              order: { id: orderId, status: orderStatus },
            },
            id: ticketId,
          } = ticket;

          const data = {
            orderId,
            ticketId,
            orderStatus,
            userId,
          };
          AppInsights.trackEvent('mdt-order-update-received', data);
        });
        // set next since parameter to the newest known modification time,
        // minus a bit of slack (1 sec)
        // as sometimes the backend skips tickets
        // (most likely due to asynchronously delivered out-of-order
        // ticket events in the event bus). we want to be really sure to 'drain' each time segment
        // of tickets as we don't want to skip any.
        const newestModificationTime = tickets
          .map(t => t.modificationTime)
          .sort(compareTimeNewestFirst)[0];
        const d = new Date(newestModificationTime);

        d.setSeconds(d.getSeconds() - 10);

        const newestModificationTimeMinusOneSec = d.toISOString();

        if (since === newestModificationTimeMinusOneSec) {
          nextSince = newestModificationTime;
        } else {
          nextSince = newestModificationTimeMinusOneSec;
        }
      }
    } catch (e) {
      Raven.captureException(e, { extra: { tickets } });
    } finally {
      setTimeout(() => dispatch(fetchTickets(nextSince)), fetchTicketsDelay);
    }
  });
};

export const startFetchingTickets = () => dispatch => {
  dispatch(fetchTicketsByQueue());
};

const getOrderWithTicketId = (getState, ticketId) => {
  const orderList = values(getState().orders.dict);
  return orderList.find(order => order.currentTicket.id === ticketId);
};
const getETag = (getState, ticketId) => getOrderWithTicketId(getState, ticketId).eTag;

const getTrackingData = (getState, order, ticketId) => {
  const {
    user: { username, account },
  } = getState();
  const userId = `${username}@${account}`;
  const { id: orderId, status: orderStatus } = order;
  return { orderId, ticketId, orderStatus, userId };
};

const trackOrderChangeData = (eventName, getState, ticketId) => {
  return () => {
    const {
      user: { username, account },
      dealer: { all: dealerList },
    } = getState();

    const order = getOrderWithTicketId(getState, ticketId);
    const { dealerId, id: orderId, status: orderStatus } = order;
    const userId = `${username}@${account}`;
    const isDealerActive = dealerList[dealerId].isActive;

    track(eventName, {
      location: 'OrderHandling',
      type: 'button',
      userId,
      dealerId,
      orderId,
      orderStatus,
      isDealerActive,
      ticketId,
    });
  };
};

export const assignTicketToCurrentUser = ticketId => (dispatch, getState, { api }) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startExecutingAssignment());
  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    const {
      currentTicket: { eTag },
    } = getOrderWithTicketId(getState, ticketId);

    return api
      .assignTicketToCurrentUser(ticketId, eTag, trackingData)
      .then(() => dispatch(doneExecutingAssignment()))
      .catch(e => {
        dispatch(doneExecutingAssignment());
        dispatch(processingOrderError(orderId));

        throw new Error(e);
      });
  });
};

export const unAssignTicketToCurrentUser = ticketId => (dispatch, getState, { api }) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startExecutingAssignment());
  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    const {
      currentTicket: { eTag },
    } = getOrderWithTicketId(getState, ticketId);

    return api
      .unAssignTicketToCurrentUser(ticketId, eTag, trackingData)
      .then(() => dispatch(doneExecutingAssignment()))
      .catch(e => {
        dispatch(doneExecutingAssignment());
        dispatch(processingOrderError(orderId));

        throw new Error(e);
      });
  });
};

export const setCurrentOrder = id => {
  return dispatch => {
    dispatch({
      type: SET_CURRENT_ORDER_ID,
      currentOrderId: id,
    });
  };
};

export const startDelivery = (ticketId, etaMinutes, etaWindow, driverId) => (
  dispatch,
  getState,
  { api },
) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    return api
      .startDelivery(
        ticketId,
        getETag(getState, ticketId),
        etaMinutes,
        etaWindow,
        driverId,
        trackingData,
      )
      .then(trackOrderChangeData('Order Start Delivery', getState, ticketId))
      .catch(e => {
        dispatch(processingOrderError(orderId));
        throw new Error(e);
      });
  });
};

export const declineOrder = (ticketId, reason, message) => (dispatch, getState, { api }) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    return api
      .declineOrder(ticketId, getETag(getState, ticketId), { reason, message }, trackingData)
      .then(trackOrderChangeData('Order Declined', getState, ticketId))
      .catch(e => {
        dispatch(processingOrderError(orderId));
        throw new Error(e);
      });
  });
};

export const deliverOrder = ticketId => (dispatch, getState, { api }) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    return api
      .deliverOrder(ticketId, getETag(getState, ticketId), trackingData)
      .then(trackOrderChangeData('Order Deliver', getState, ticketId))
      .catch(e => {
        dispatch(processingOrderError(orderId));
        throw new Error(e);
      });
  });
};

export const cancelOrder = (ticketId, reason, message) => (dispatch, getState, { api }) => {
  const order = getOrderWithTicketId(getState, ticketId);
  const { orderId } = order;
  const trackingData = getTrackingData(getState, order, ticketId);

  dispatch(startProcessingOrder(orderId));

  return executeTicketCommand(ticketId, () => {
    return api
      .cancelOrder(ticketId, getETag(getState, ticketId), { reason, message }, trackingData)
      .then(trackOrderChangeData('Order Canceled', getState, ticketId))
      .catch(e => {
        dispatch(processingOrderError(orderId));
        throw new Error(e);
      });
  });
};

export const dismissOrder = (ticketId, eTag) => (dispatch, getState, { api }) => {
  const { id } = getOrderWithTicketId(getState, ticketId);

  dispatch(startProcessingOrder(id));

  return executeTicketCommand(ticketId, () => {
    return api
      .dismissOrder(ticketId, eTag)
      .then(trackOrderChangeData('Order Dismissed', getState, ticketId))
      .catch(e => {
        dispatch(processingOrderError(id));
        throw new Error(e);
      });
  });
};

export const cancelDelivery = (ticketId, eTag) => (dispatch, getState, { api }) => {
  const { id } = getOrderWithTicketId(getState, ticketId);

  dispatch(startProcessingOrder(id));

  return executeTicketCommand(ticketId, () => {
    return api
      .cancelDelivery(ticketId, eTag)
      .then(trackOrderChangeData('Order Cancel Delivery', getState, ticketId))
      .catch(e => {
        throw new Error(e);
      });
  });
};

export const startFetchingDrivers = ticketId => (dispatch, getState) => {
  const { dealerId, lat, lon } = getOrderWithTicketId(getState, ticketId);

  return dispatch(fetchDrivers(dealerId, lat, lon));
};



// WEBPACK FOOTER //
// ./src/actions/orders.js